import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, map, of, switchMap, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import type {
  AjustarLogAcceso,
  AjustarLogAccesoRequest,
  JsModulo,
  LogAcceso,
} from './interfaces/auth.interface';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private base = `${environment.apiUrl}/auth`;

  readonly sesion = signal<LogAcceso | null>(null);
  readonly moduloActual = computed(() =>
    this.sesion()?.modulosAbiertos.find((m) => m.cveModulo === environment.cveModulo),
  );

  getLogAcceso(): Observable<LogAcceso> {
    return this.http.post<LogAcceso>(`${this.base}/getLogAcceso`, {});
  }

  /**
   * Deja la sesión con este módulo abierto. Si se entró directo por URL estando en
   * otro módulo, entra con la organización de ese módulo; la API valida que la
   * organización tenga contratado este.
   */
  entrarAlModulo(): Observable<LogAcceso> {
    return this.getLogAcceso().pipe(
      switchMap((sesion) => {
        if (sesion.modulosAbiertos.some((m) => m.cveModulo === environment.cveModulo)) {
          return of(sesion);
        }

        const organizacionId = sesion.modulosAbiertos[0]?.organizacionId;
        if (!organizacionId) return throwError(() => new Error('Sin organización'));

        return this.http.post<JsModulo[]>(`${this.base}/getJsModulos`, { organizacionId }).pipe(
          map((modulos) => modulos.find((m) => m.cve === environment.cveModulo)),
          switchMap((modulo) =>
            modulo
              ? this.ajustarLogAcceso({
                  status: 'Login',
                  moduloId: modulo.moduloId,
                  organizacionId,
                })
              : throwError(() => new Error('La organización no tiene este módulo')),
          ),
          switchMap(() => this.getLogAcceso()),
        );
      }),
      tap((sesion) => this.sesion.set(sesion)),
    );
  }

  ajustarLogAcceso(datos: AjustarLogAccesoRequest): Observable<AjustarLogAcceso> {
    return this.http.post<AjustarLogAcceso>(`${this.base}/ajustarLogAcceso`, datos);
  }

  // Pase lo que pase se regresa al login: si la sesión ya estaba cerrada, la API responde 401.
  cerrarSesion(rutaLogin = ''): void {
    this.ajustarLogAcceso({ status: 'Exit_Logout' }).subscribe({
      next: () => this.irAlLogin(rutaLogin),
      error: () => this.irAlLogin(rutaLogin),
    });
  }

  // replace() para que el botón "atrás" no regrese a una pantalla sin sesión.
  irAlLogin(ruta = ''): void {
    this.sesion.set(null);
    window.location.replace(environment.loginUrl + ruta);
  }
}
